// Kanzlei-Übersicht — alle Kunden auf einen Blick + „Neue Kanzlei" anlegen.

import { useCallback, useEffect, useState } from "react";

import { Loader } from "@/components/Loader";

import { getFirms, OperatorApiError, type FirmSummary } from "./api";
import { NewFirmForm } from "./NewFirmForm";

export function FirmsPage({
  onSelect,
  onExpired,
}: {
  onSelect: (id: string) => void;
  onExpired: () => void;
}) {
  const [firms, setFirms] = useState<FirmSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [query, setQuery] = useState("");

  const load = useCallback(() => {
    setFirms(null);
    setError(null);
    getFirms()
      .then(setFirms)
      .catch((e: unknown) => {
        if (e instanceof OperatorApiError && e.status === 401) onExpired();
        else setError(e instanceof Error ? e.message : "Fehler beim Laden");
      });
  }, [onExpired]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section>
      <div className="op-toolbar">
        <h1 className="op-h1">
          Kanzleien {firms && <span className="op-count">{firms.length}</span>}
        </h1>
        {!showNew && (
          <button
            type="button"
            className="op-btn"
            onClick={() => setShowNew(true)}
          >
            + Neue Kanzlei
          </button>
        )}
      </div>

      {showNew && (
        <NewFirmForm
          onDone={() => {
            setShowNew(false);
            load();
          }}
          onCancel={() => setShowNew(false)}
        />
      )}

      {error && <p className="op-error">{error}</p>}

      {firms && firms.length > 0 && (
        <input
          className="op-search"
          type="search"
          placeholder="Suchen (Name, Kürzel, Plan)…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}

      {!firms ? (
        <Loader label="Kanzleien werden geladen…" />
      ) : firms.length === 0 ? (
        <p className="op-empty">Noch keine Kanzlei angelegt.</p>
      ) : (
        <table className="op-table">
          <thead>
            <tr>
              <th>Kanzlei</th>
              <th>Plan</th>
              <th>Sachbearbeiter</th>
              <th>Installation</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {firms
              .filter((f) =>
                `${f.name} ${f.slug} ${f.plan}`
                  .toLowerCase()
                  .includes(query.trim().toLowerCase())
              )
              .map((f) => (
              <tr
                key={f.id}
                className="op-row"
                onClick={() => onSelect(f.id)}
                title="Details ansehen"
              >
                <td>
                  <strong>{f.name}</strong>
                  <div className="op-slug">{f.slug}</div>
                </td>
                <td>
                  <span className="op-plan">{f.plan}</span>
                  {!f.active && <span className="op-inactive"> · inaktiv</span>}
                </td>
                <td>{f.user_count}</td>
                <td>
                  {f.installation_count > 0
                    ? `${f.installation_count} ✓`
                    : "—"}
                </td>
                <td className="op-chevron">›</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
